// Placeable scope options — the "model-based interview".
//
// Some scope items aren't a single yes/no responsibility; they're deployed to
// LOCATIONS with a chosen MATERIAL/TYPE and a QUANTITY that falls out of the
// room geometry (e.g. wall protection: vinyl in the corridors, stainless in the
// material airlock, bumper rail in a specific lab). `optionSetForItem()` below
// still matches a register ScopeItem to this by keyword — no DB wiring needed
// just to make an item PLACEABLE at all — but which MATERIAL is applied, and
// its pricing, are now real EstimateCostItems catalog rows (Phase E of
// consolidating Condition Library/ScopeApplications into Items/Assemblies —
// see project memory; db/58_scope_application_items.sql), not a fixed option
// key — `deriveQuantity()`/`appQuantity()` below read a placement's basis/
// dimensions off its own resolved catalog row directly. `SCOPE_OPTION_SETS`'s
// `options` array (with `optionByKey()`) is kept only for TakeoffCanvas.jsx's
// legacy rendering of already-drawn `TargetType='drawn'` runs, whose
// `OptionKey` was preserved (not cleared) by Phase E's migration — the
// Scope & RACI interview itself no longer reads either.

import { ROOM_TYPES } from "../utils/takeoffMath";

// basis: how the quantity derives from a target room
//   perimeterLF → Σ 2·(L+W)              (linear runs: rails, base)
//   wallSF      → Σ 2·(L+W)·H            (sheet goods: vinyl, SS panel)
//   manual      → estimator enters it    (counts: corner guards)
export const SCOPE_OPTION_SETS = [
  {
    key: "wall_protection",
    label: "Wall protection",
    match: (item) =>
      /wall protection|bumper rail|crash rail|wall guard|corner guard|sani-?rail|wainscot/i.test(item?.Activity || ""),
    options: [
      { key: "vinyl",  label: "Vinyl wall protection",          unit: "SF", basis: "wallSF", color: "#0d9488", defaultHeightFt: 8 },
      { key: "ss",     label: "Stainless steel wall protection", unit: "SF", basis: "wallSF", color: "#6366f1", defaultHeightFt: 8 },
      { key: "bumper", label: "Floor-mounted bumper rail",       unit: "LF", basis: "perimeterLF", color: "#d97706" },
      { key: "corner", label: "Corner guards",                   unit: "EA", basis: "manual", color: "#dc2626" },
    ],
  },
];

export function optionSetForItem(item) {
  return SCOPE_OPTION_SETS.find((s) => s.match(item)) || null;
}

export function optionByKey(set, key) {
  return set?.options.find((o) => o.key === key) || null;
}

const roomTypeLabel = (v) => ROOM_TYPES.find((t) => t.value === v)?.label || v;

// Rooms a target resolves to. targetKey is a room-type value ('corridor'…) for
// 'room_type', a RoomID for 'room', ignored for 'all_rooms'.
export function targetRooms(targetType, targetKey, rooms) {
  if (targetType === "all_rooms") return rooms;
  if (targetType === "room_type") return rooms.filter((r) => r.RoomType === targetKey);
  if (targetType === "room") return rooms.filter((r) => String(r.RoomID) === String(targetKey));
  return []; // 'drawn' handled from geometry, not room set
}

// Live quantity for an application, from the referenced catalog item's own
// ScopeQuantityBasis (Phase E of consolidating Condition Library/
// ScopeApplications into Items/Assemblies — see project memory;
// db/58_scope_application_items.sql) + its target rooms. Returns null when
// the basis can't be derived (manual / drawn) so the UI shows the manual
// field instead.
export function deriveQuantity(item, targetType, targetKey, rooms) {
  if (!item || item.ScopeQuantityBasis === "manual" || targetType === "drawn") return null;
  let q = 0;
  for (const r of targetRooms(targetType, targetKey, rooms)) {
    const L = +r.LengthFt, W = +r.WidthFt, H = +r.HeightFt;
    if (!(L > 0) || !(W > 0)) continue;
    const perim = 2 * (L + W);
    if (item.ScopeQuantityBasis === "perimeterLF") q += perim;
    else if (item.ScopeQuantityBasis === "wallSF") q += perim * (H > 0 ? H : 0);
  }
  return Math.round(q);
}

// Parse a drawn run's stored geometry: { points:[{x,y}…], lengthFt, heightFt? }
export function parseGeom(app) {
  if (!app?.GeometryJson) return null;
  try { return JSON.parse(app.GeometryJson); } catch { return null; }
}

// Total plan length of a polyline in world feet
export function polylineLength(points) {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return len;
}

// One function both the register and the canvas use, so quantities never
// diverge. Returns { qty, derived } — derived=false means the estimator must
// enter it (manual item, no rooms). Drawn runs measure from their geometry.
// `item` is the application's own resolved catalog entity (from
// sp_GetScopeApplicationEntities/sp_GetScopeTargetableItems — Phase E, see
// project memory), not the legacy hardcoded option object.
export function appQuantity(item, app, rooms) {
  if (!item) return { qty: app?.QuantityOverride ?? null, derived: false };
  if (app.QuantityOverride != null) return { qty: app.QuantityOverride, derived: false };

  if (app.TargetType === "drawn") {
    const g = parseGeom(app);
    const len = g?.lengthFt || 0;
    if (item.ScopeQuantityBasis === "wallSF") return { qty: Math.round(len * (g?.heightFt || item.DefaultHeightFt || 8)), derived: true };
    return { qty: Math.round(len), derived: true }; // perimeterLF / manual → LF of the run
  }

  if (item.ScopeQuantityBasis === "manual") return { qty: null, derived: false };
  return { qty: deriveQuantity(item, app.TargetType, app.TargetKey, rooms), derived: true };
}

// Human label for a target, e.g. "All Corridor rooms" / a room name / "All rooms".
export function targetLabel(targetType, targetKey, rooms) {
  if (targetType === "all_rooms") return "All rooms";
  if (targetType === "room_type") return `All ${roomTypeLabel(targetKey)} rooms`;
  if (targetType === "room") {
    const r = rooms.find((x) => String(x.RoomID) === String(targetKey));
    return r ? r.RoomName : "Room";
  }
  if (targetType === "drawn") return "Drawn run";
  return targetKey || "";
}

// Target choices offered in the interview, given the rooms present.
//
// "Target by room type" (Room Templates Phase F-2) is deliberately no longer
// offered here for NEW scope applications, now that RoomType itself is being
// retired as a room's primary classification (Room Templates replace it —
// see project memory). Removed here rather than replaced with "by template":
// a template list is admin-editable/unbounded, unlike the old fixed ROOM_TYPES
// enum, so "all rooms of this template" would need its own real feature, not
// a drop-in swap — deferred, not attempted piecemeal. targetRooms()/
// targetLabel() below still resolve and price an ALREADY-configured
// room_type-targeted application exactly as before (untouched) — this only
// removes the choice from the picker for anything created going forward.
export function targetChoices(rooms) {
  const roomChoices = rooms
    .filter((r) => +r.LengthFt > 0 && +r.WidthFt > 0)
    .map((r) => ({ targetType: "room", targetKey: String(r.RoomID), label: r.RoomName }));
  return {
    quick: [{ targetType: "all_rooms", targetKey: null, label: "All rooms" }],
    rooms: roomChoices,
  };
}
